/**
 * Solo practice — free, single-player, no opponent and no stake.
 *
 * Deliberately stripped down vs the duel page: no stake pills, no
 * winner-payout card, no invite flow, no "got a code" form. Just pick
 * a category + difficulty and tap Start.
 */

import { api, ApiError } from '../api.js';
import { attachCategoryPicker, renderCategoryTrigger } from '../components/category_picker.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';

function pickRandom(items) {
  return items[Math.floor(Math.random() * items.length)] || null;
}

/**
 * Resolve the initial category selection: honour `?category_id=` in the
 * URL when it points at a real category (the live-detail "Warm up in
 * Solo" nudge deep-links this way so a player can practise the trivia's
 * topic first). Falls back to a random pick when the param is missing
 * or points at a category that's been renamed / deleted between the
 * two page navigations.
 */
function pickInitialCategory(categories) {
  try {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('category_id');
    if (requested) {
      const match = categories.find((c) => c.id === requested);
      if (match) return match;
    }
  } catch {
    // URL parsing shouldn't ever throw in practice — fall through.
  }
  return pickRandom(categories);
}

export async function renderSolo(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/play" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Solo practice</div>
    </div>
    <div class="pad" id="solo-page">
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  const page = document.getElementById('solo-page');
  let categories = [];
  try {
    categories = await api('/game/categories');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err);
    page.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }
  if (!categories.length) {
    page.innerHTML = `
      <div class="card muted">
        No categories yet. Ask an admin to <a href="/admin/questions" data-link>add one</a>.
      </div>
    `;
    return;
  }

  let selectedCategory = pickInitialCategory(categories);
  let selectedDifficulty = 'medium';

  page.innerHTML = `
    <div class="hero violet">
      <span class="tag" style="background:rgba(255,255,255,0.18);color:#fff;">🧠 PRACTICE</span>
      <div style="font-family:var(--font-display);font-size:18px;font-weight:600;margin:9px 0 2px;">
        Solo practice
      </div>
      <div style="font-size:12px;font-weight:500;opacity:0.9;">
        10 questions. Free. No stakes — just learn at your own pace.
      </div>
    </div>

    <h3 class="sec">Category</h3>
    ${renderCategoryTrigger('solo-cat-picker', { placeholder: 'Choose category' })}

    <h3 class="sec">Difficulty</h3>
    <div id="diff-pills" class="topic-grid">
      ${['easy', 'medium', 'hard']
        .map((d) => `<span class="pill diff-pill" data-d="${d}">${d}</span>`)
        .join('')}
    </div>

    <div class="cta-row">
      <button type="button" id="btn-solo" class="btn gold">Start solo game</button>
    </div>

    <div id="solo-status"></div>
  `;

  const diffPills = document.getElementById('diff-pills');
  refreshPills(diffPills, 'diff-pill', selectedDifficulty, 'd');

  attachCategoryPicker('solo-cat-picker', {
    categories,
    selected: selectedCategory,
    onChange: (cat) => {
      selectedCategory = cat;
    },
  });

  diffPills.addEventListener('click', (e) => {
    const pill = e.target.closest('.diff-pill');
    if (!pill) return;
    selectedDifficulty = pill.dataset.d;
    refreshPills(diffPills, 'diff-pill', selectedDifficulty, 'd');
  });

  document.getElementById('btn-solo').addEventListener('click', async () => {
    setSoloStatus('Starting solo game…');
    try {
      const match = await api('/match/solo', {
        method: 'POST',
        body: {
          category_id: selectedCategory.id,
          difficulty: selectedDifficulty,
          question_count: 10,
        },
      });
      navigate(`/match/${match.id}`);
    } catch (err) {
      const msg = err.message || 'Could not start solo game.';
      setSoloStatus(msg, 'error');
      toast.error(msg);
    }
  });
}

function refreshPills(host, cls, selectedKey, dataAttr) {
  if (!host) return;
  host.querySelectorAll('.' + cls).forEach((p) => {
    p.classList.toggle('on', p.dataset[dataAttr] === selectedKey);
  });
}

function setSoloStatus(message, kind = 'info') {
  const el = document.getElementById('solo-status');
  if (!el) return;
  if (!message) { el.innerHTML = ''; return; }
  const color = kind === 'error' ? 'var(--pink-t)' :
                kind === 'success' ? 'var(--teal-t)' :
                'var(--ink-2)';
  el.innerHTML = `<div style="color:${color};margin-top:11px;font-weight:600;font-size:12.5px;">${escape(message)}</div>`;
}
